export interface Frontmatter {
  title: string;
  description?: string;
  order?: number;
  tags?: string[];
  author?: string;
  lastUpdated?: string;
  [key: string]: unknown; // Allow additional frontmatter fields
}

export interface ParsedMarkdown {
  frontmatter: Frontmatter;
  content: string;
  excerpt?: string;
}

/**
 * Simple YAML frontmatter parser for browser (doesn't require Buffer)
 * Parses basic YAML frontmatter - handles strings, numbers, booleans, and arrays
 */
function parseYAML(yaml: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const lines = yaml.split("\n");
  let currentKey = "";
  let currentValue: string[] = [];
  let inArray = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines and comments
    if (!trimmed || trimmed.startsWith("#")) {
      continue;
    }

    // Check if this is an array item (starts with -)
    if (trimmed.startsWith("-")) {
      if (currentKey) {
        const item = trimmed.substring(1).trim();
        const cleanedItem = item.replace(/^["']|["']$/g, "");
        currentValue.push(cleanedItem);
        inArray = true;
      }
      continue;
    }

    // If we were building an array, save it
    if (inArray && currentKey) {
      result[currentKey] = currentValue;
      currentKey = "";
      currentValue = [];
      inArray = false;
    }

    const colonIndex = trimmed.indexOf(":");
    if (colonIndex === -1) continue;

    const key = trimmed.substring(0, colonIndex).trim();
    let value: unknown = trimmed.substring(colonIndex + 1).trim();

    // Check if value starts with [ indicating inline array
    if (
      typeof value === "string" &&
      value.startsWith("[") &&
      value.endsWith("]")
    ) {
      const arrayContent = value.slice(1, -1).trim();
      if (arrayContent) {
        value = arrayContent
          .split(",")
          .map((item) => item.trim().replace(/^["']|["']$/g, ""));
      } else {
        value = [];
      }
      result[key] = value;
      continue;
    }

    // Check if next line starts with - (multiline array)
    if (i + 1 < lines.length && lines[i + 1].trim().startsWith("-")) {
      currentKey = key;
      currentValue = [];
      inArray = true;
      continue;
    }

    // Remove quotes if present
    if (typeof value === "string") {
      if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1);
      }
    }

    // Parse numbers
    if (typeof value === "string") {
      if (/^-?\d+$/.test(value)) {
        value = parseInt(value, 10);
      } else if (/^-?\d+\.\d+$/.test(value)) {
        value = parseFloat(value);
      }
    }

    // Parse booleans and null
    if (value === "true") value = true;
    if (value === "false") value = false;
    if (value === "null") value = null;

    result[key] = value;
  }

  // Handle trailing array
  if (inArray && currentKey) {
    result[currentKey] = currentValue;
  }

  return result;
}

/**
 * Parse markdown content with YAML frontmatter (browser-compatible)
 */
export function parseFrontmatter(markdownContent: string): ParsedMarkdown {
  // Check for frontmatter delimiter
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = markdownContent.match(frontmatterRegex);

  if (!match) {
    // No frontmatter, return content as-is with default frontmatter
    return {
      frontmatter: {
        title: "",
      },
      content: markdownContent.trim(),
    };
  }

  const yamlContent = match[1];
  const content = match[2];

  // Check for excerpt separator
  const excerptMatch = content.match(/<!--excerpt-->\s*\n([\s\S]*?)(?:\n|$)/);
  const excerpt = excerptMatch ? excerptMatch[1].trim() : undefined;

  const frontmatter = parseYAML(yamlContent) as Frontmatter;

  return {
    frontmatter,
    content: content.trim(),
    excerpt: excerpt || frontmatter.description || undefined,
  };
}

/**
 * Validate frontmatter has required fields
 */
export function validateFrontmatter(frontmatter: Frontmatter): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!frontmatter.title || typeof frontmatter.title !== "string") {
    errors.push("Frontmatter must include a 'title' field");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
